/**
 * Панель «Сьогодні» (start.html; components.md, розд. 11; tasks-today.md, розд. 3 і 6):
 * Прострочено → На сьогодні з лінією «зараз» → Чекають першої реакції → Виконано (згорнуто).
 * Дані — `GET /api/v1/activities/today` після входу (`source.ts`).
 * Сьогоднішня задача переходить у «Прострочено» о `overdueAt` сама (`arrange`), північ і
 * повернення у вкладку перечитують панель (`live.ts`).
 * Виконати — 1 клік або `X`, тост «Скасувати» 5 с, фокус переходить на наступну задачу. Перенести —
 * меню або `S`, варіанти рахує сервер. Дзвінок видно завжди.
 * Блукаючий tabindex: `Tab` заходить у список один раз, далі J/K або ↑/↓ (keyboard.md, розд. 6).
 */
import type { RecordRow, TodayItem } from '@clientline/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type KeyboardEvent, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { focusKeyAllowed, focusShiftKeyAllowed, useHotkeys } from '../../app/hotkeys.ts';
import { TODAY_KEY_HINTS } from '../../app/keymap.ts';
import { useNow } from '../../app/now.ts';
import {
  dayDiff,
  formatFullDay,
  formatOverdueLabel,
  formatTime,
  useRegionKey,
} from '../../i18n/format.ts';
import { common as tc } from '../../i18n/uk/common.ts';
import { list as tl } from '../../i18n/uk/list.ts';
import { outcome as tout } from '../../i18n/uk/outcome.ts';
import { today as t } from '../../i18n/uk/today.ts';
import { markAfterPaint, markOnceAfterPaint, PERF_MARKS } from '../../perf/marks.ts';
import { newKey } from '../../platform/api.ts';
import { entityOf, usePlatformMeta } from '../../platform/meta.ts';
import { runOptimistic } from '../../platform/optimistic.ts';
import { useTodayPanel } from '../../shell/today-context.ts';
import { Icon } from '../../ui/Icon.tsx';
import { MenuButton } from '../../ui/Menu.tsx';
import { useToast } from '../../ui/notify/index.ts';
import { Pending } from '../../ui/Pending.tsx';
import { StateView } from '../../ui/StateView.tsx';
import { beginTelCall } from '../call/active.ts';
import { callEntityOf } from '../call/call-card.ts';
import { fetchNoOutcome, NO_OUTCOME_LIMIT, noOutcomeKey } from '../call/no-outcome.ts';
import { startCall, telHref } from '../call/start-call.ts';
import type { CompletedTask } from '../tasks/outcome/TaskOutcomeSheet.tsx';
import { hasOutcomes, taskTypesOf, typeOf } from '../tasks/outcome/types.ts';
import { myTasksView } from '../tasks/views.ts';
import { todayTelCall } from './call.ts';
import {
  arrange,
  CATEGORY_ICON,
  completeIn,
  type DueChoice,
  dueChoices,
  NAV_COUNT_MAX,
  rescheduleIn,
  restoreIn,
  sectionOf,
  type TaskSection,
  type TodayData,
  type TodayDue,
  type WaitingItem,
  withVersion,
} from './model.ts';
import { TASK_ENTITY, type TodaySource, type TodayWrite, todayQueryOf } from './source.ts';

/** Панель результату — лінивий чанк (E8a-W3): на перший екран «Сьогодні» не впливає. */
const loadSheets = () => import('../tasks/outcome/sheets.ts');
const TaskOutcomeSheet = lazy(() => loadSheets().then((m) => ({ default: m.TaskOutcomeSheet })));

export function TodayPanel({ onCollapse }: { onCollapse: () => void }) {
  useRegionKey();
  const now = useNow();
  const { source } = useTodayPanel();
  // «Усі задачі» — подання «Сьогодні» з «Моїх задач» (E8a-W2), коли воно є в `/meta`.
  const meta = usePlatformMeta({ optional: true });
  const allTasks = meta.data ? myTasksView(meta.data) : null;

  return (
    <section className="today" aria-labelledby="today-title">
      <div className="today__head">
        <h2 className="today__title" id="today-title">
          {t.todayTitle}
        </h2>
        <span className="muted small truncate">{formatFullDay(now)}</span>
        <span className="spacer" />
        {allTasks && (
          <Link
            className="btn btn--ghost btn--sm"
            to="/l/$view"
            params={{ view: allTasks.key }}
            data-testid="today-all-tasks"
          >
            {t.todayAllTasks}
          </Link>
        )}
        <button
          className="btn btn--ghost btn--icon btn--sm"
          type="button"
          aria-label={t.todayCollapse}
          onClick={onCollapse}
        >
          <Icon name="chevron-right" />
        </button>
      </div>
      <div className="today__body">{source && <TodayBody source={source} now={now} />}</div>
      <div className="today__foot" aria-hidden="true">
        <span>
          <kbd>J</kbd> <kbd>K</kbd> {t.hintMove}
        </span>
        <span>
          <kbd>X</kbd> {t.hintDone}
        </span>
        <span>
          <kbd>S</kbd> {t.hintSnooze}
        </span>
        <span>
          <kbd>C</kbd> {t.hintCall}
        </span>
        <span>
          <kbd>Enter</kbd> {t.hintOpen}
        </span>
      </div>
    </section>
  );
}

function TodayBody({ source, now }: { source: TodaySource; now: number }) {
  const query = useQuery(todayQueryOf(source));
  useEffect(() => {
    if (query.data) markOnceAfterPaint(PERF_MARKS.todayReady);
  }, [query.data]);

  if (query.data) return <TodayLists source={source} data={query.data} now={now} />;
  if (query.isError) {
    // Збій — лише панелі (tasks-today.md, розд. 3): решта екрана працює, код — для звернення.
    return (
      <StateView
        icon="alert-circle"
        tone="error"
        title={t.errorTodayTitle}
        text={t.errorTodayText}
        code={t.errorTodayCode}
      >
        <button className="btn" type="button" onClick={() => void query.refetch()}>
          {tc.retry}
        </button>
      </StateView>
    );
  }
  return <Pending skeleton={<TodaySkeleton />} onRetry={() => void query.refetch()} />;
}

/** Рядок задачі словами: хто чи що — головне, контекст — запис і корінь (start.html). */
export function describeItem(item: TodayItem): {
  title: string;
  prefix: string | null;
  ctx: string;
} {
  const person = item.root?.label ?? null;
  const host = item.host?.label ?? null;
  const fallback = item.subject ?? t.category[item.category];
  if (item.host === null) return { title: fallback, prefix: null, ctx: t.personalTask };
  // Дзвінок — про людину: її ім'я в рядку, тема — для програми читання з екрана.
  if (item.category === 'call' && (person ?? host)) {
    return {
      title: person ?? (host as string),
      prefix: item.subject,
      ctx: person ? (host ?? '') : '',
    };
  }
  return { title: fallback, prefix: null, ctx: [person, host].filter(Boolean).join(' · ') };
}

/**
 * Час у рядку: час; прострочена з минулого дня — день («Учора», «Пн»), сьогоднішня, чий час минув
 * (TSK-17, о 14:00), — той самий час червоним. «Увесь день» у колонці 40 px — «—» (start.html), а
 * словом — у доступній назві рядка (`spoken`).
 */
function timeOf(item: TodayItem, overdue: boolean, now: number): { shown: string; spoken: string } {
  if (!item.dueAt) return { shown: tc.noTime, spoken: tc.noTime };
  if (overdue && dayDiff(item.dueAt, now) < 0) {
    const day = formatOverdueLabel(item.dueAt, now);
    return { shown: day, spoken: day };
  }
  if (item.allDay) return { shown: tc.noTime, spoken: t.allDay };
  const time = formatTime(item.dueAt);
  return { shown: time, spoken: time };
}

function TodayLists({ source, data, now }: { source: TodaySource; data: TodayData; now: number }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { overdue, today, done, overdueCount, todayCount, doneCount, nowIndex } = arrange(
    data,
    now,
  );
  const [doneOpen, setDoneOpen] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [snoozeSignal, setSnoozeSignal] = useState<{ id: string; n: number } | null>(null);
  const [outcomeFor, setOutcomeFor] = useState<TodayItem | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Типи задач з `/meta` (кеш застосунку): чи є в типу результати для «Виконати з результатом…».
  const meta = usePlatformMeta({ optional: true });
  const types =
    source.outcomePanel && meta.data ? taskTypesOf(entityOf(meta.data, TASK_ENTITY)) : null;
  const withOutcome = (item: TodayItem) =>
    source.outcomePanel === true &&
    item.state === 'open' &&
    item.actions.includes('complete') &&
    (item.outcomeRequired || hasOutcomes(typeOf(types, item.type)));
  const order = [...overdue, ...today];
  const activeId =
    focusId && order.some((x) => x.id === focusId) ? focusId : (order[0]?.id ?? null);

  const update = (change: (old: TodayData) => TodayData) =>
    queryClient.setQueryData<TodayData>(source.key, (old) => (old ? change(old) : old));

  const focusTask = (id: string | undefined) => {
    if (!id) return;
    setFocusId(id);
    requestAnimationFrame(() =>
      listRef.current?.querySelector<HTMLElement>(`[data-task="${id}"]`)?.focus(),
    );
  };

  /**
   * Оптимістично (`runOptimistic`): панель змінюється одразу, відмова повертає зміну з тостом і
   * «Ще раз». Зміни однієї задачі (виконати → «Скасувати») ідуть на сервер по черзі, тож
   * «Скасувати» бере токен відповіді, що вже прийшла. Після відповіді — нова версія задачі
   * (передумова наступної дії) і перечитування: секції й лічильники рахує сервер.
   */
  const run = (
    item: TodayItem,
    change: (old: TodayData) => TodayData,
    call: (key: string) => Promise<TodayWrite | { version: number | null }>,
    applied?: () => void,
  ) => {
    const key = newKey();
    let result: TodayWrite | null = null;
    void runOptimistic(queryClient, {
      scope: `today:${item.id}`,
      keys: [source.key],
      // Тост «· Скасувати» і фокус — лише разом зі зміною: офлайн її немає, і «Виконано» було б
      // неправдою поруч із «Немає зв'язку».
      apply: () => {
        update(change);
        applied?.();
      },
      send: async () => {
        const answer = await call(key);
        result = { version: answer.version, undo: 'undo' in answer ? answer.undo : null };
        if (answer.version !== null)
          update((old) => withVersion(old, item.id, answer.version as number));
      },
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: source.key, exact: true }),
      failText: tout.saveFailed,
    });
    return { token: () => (result as TodayWrite | null)?.undo ?? null };
  };

  /** Тост з «Скасувати»: повертає задачу, де була, і шле скасування токеном цієї дії. */
  const toastUndo = (
    item: TodayItem,
    from: TaskSection,
    text: string,
    token: () => string | null,
  ) =>
    toast({
      // Ключ — область зміни: відмова замінить це «· Скасувати», бо воно вже неправда.
      key: `today:${item.id}`,
      text,
      action: {
        label: tc.undo,
        onAction: () =>
          void run(
            item,
            (old) => restoreIn(old, item, from),
            (key) => {
              const undo = token();
              if (!undo) return Promise.reject(new Error('undo_unavailable'));
              return source.undo(undo, key);
            },
          ),
      },
    });

  const openRecord = (item: TodayItem, result?: 'call' | 'log') => {
    const target = item.host ?? { entity: 'activity', id: item.id };
    void navigate({
      to: '/r/$entity/$recordId',
      params: { entity: target.entity, recordId: target.id },
      search: result ? { result } : {},
    });
  };

  /**
   * «Подзвонити» (Г2-W1b; web-calls.md, розд. 2): номер уже скопійовано в обробнику кліку, набір
   * відкривається посиланням `tel:`. Дзвінок записується на господаря задачі з номером кореня і цією
   * задачею-планом (`todayTelCall`), а картка господаря одразу показує рядок дзвінка й блок
   * результату (`active.ts`). Без запису (fail closed) — лише картка господаря, як до Г2-W1b.
   */
  const call = (item: TodayItem) => {
    const tel = todayTelCall(item, meta.data);
    if (tel) {
      beginTelCall(tel, item.phone ?? '');
      // Блок результату вантажиться, поки відкривається софтфон (web-calls.md, розд. 5).
      void loadSheets().catch(() => {
        // Мережа впала — `lazy` картки спробує ще раз, коли блок знадобиться.
      });
    }
    openRecord(item);
  };

  /** Панель результату на місці (TSK-14): список поруч лишається видно. */
  const openOutcome = (item: TodayItem) => {
    setFocusId(item.id);
    setOutcomeFor(item);
  };

  /**
   * Результат збережено (`complete` з результатом і кроком): задача — у «Виконано», нова версія,
   * перечитування (крок на сьогодні з'явиться в панелі) і тост «Скасувати» — токен повертає і
   * задачу, і створений крок однією дією (tasks.md, розд. 4).
   */
  const completed = (item: TodayItem, result: CompletedTask) => {
    const from = sectionOf(data, item.id) ?? 'today';
    const next = order[order.findIndex((x) => x.id === item.id) + 1];
    update((old) => withVersion(completeIn(old, item.id), item.id, result.version));
    markAfterPaint(PERF_MARKS.saveDone);
    void queryClient.invalidateQueries({ queryKey: source.key, exact: true });
    toastUndo(item, from, result.text, () => result.undo);
    focusTask(next?.id);
  };

  const complete = (item: TodayItem) => {
    // Тип з обов'язковим результатом — «Виконати» відкриває форму результату (TSK-14): на місці,
    // а в джерела без панелі (`outcomePanel`) — у картці задачі.
    if (item.outcomeRequired) {
      if (source.outcomePanel) return openOutcome(item);
      return openRecord(item, item.category === 'call' ? 'call' : 'log');
    }
    const from = sectionOf(data, item.id) ?? 'today';
    const next = order[order.findIndex((x) => x.id === item.id) + 1];
    const action = run(
      item,
      (old) => completeIn(old, item.id),
      (key) => source.complete(item, key),
      () => {
        toastUndo(item, from, t.taskDone, () => action.token());
        focusTask(next?.id);
      },
    );
  };

  const snooze = (item: TodayItem, due: TodayDue) => {
    const from = sectionOf(data, item.id) ?? 'today';
    const next = order[order.findIndex((x) => x.id === item.id) + 1];
    const action = run(
      item,
      (old) => rescheduleIn(old, item.id, due),
      (key) => source.reschedule(item, due, key),
      () => {
        toastUndo(item, from, t.taskSnoozed, () => action.token());
        focusTask(next?.id);
      },
    );
  };

  // Клавіші списку обробляє onKeyDown нижче; тут — лише їхні підписи для шпаргалки `?`.
  useHotkeys(TODAY_KEY_HINTS);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // `Shift+X` — «Виконати з результатом…» (E8a-W3): правила Shift-клавіш області (LST-36).
    if (e.code === 'KeyX' && e.shiftKey && focusShiftKeyAllowed(e.nativeEvent)) {
      if ((e.target as HTMLElement).closest('[role="menu"]')) return;
      const item = order.find((x) => x.id === activeId);
      if (item && withOutcome(item)) {
        e.preventDefault();
        openOutcome(item);
      }
      return;
    }
    if (!focusKeyAllowed(e.nativeEvent)) return;
    if ((e.target as HTMLElement).closest('[role="menu"]')) return;
    const index = order.findIndex((x) => x.id === activeId);
    const item = order[index];
    const can = (action: 'complete' | 'reschedule') => item?.actions.includes(action) === true;
    const actions: Record<string, () => void> = {
      ArrowDown: () => focusTask(order[index + 1]?.id),
      KeyJ: () => focusTask(order[index + 1]?.id),
      ArrowUp: () => focusTask(order[index - 1]?.id),
      KeyK: () => focusTask(order[index - 1]?.id),
      KeyX: () => item && can('complete') && complete(item),
      KeyS: () => item && can('reschedule') && setSnoozeSignal({ id: item.id, n: Date.now() }),
      KeyC: () =>
        listRef.current
          ?.querySelector<HTMLAnchorElement>(`[data-task="${activeId}"] [data-call]`)
          ?.click(),
      Enter: () => item && openRecord(item),
    };
    const action = actions[e.code];
    if (!action || ((e.target as HTMLElement).tagName === 'BUTTON' && e.code === 'Enter')) return;
    e.preventDefault();
    action();
  };

  const choices = (item: TodayItem) => dueChoices(item, data.dueOptions, data.timeZone);
  const row = (item: TodayItem, section: TaskSection, isNow = false) => (
    <TaskRow
      key={item.id}
      item={item}
      overdue={section === 'overdue'}
      isNow={isNow}
      active={item.id === activeId}
      now={now}
      choices={item.actions.includes('reschedule') && section !== 'done' ? choices(item) : []}
      noWorkingTime={data.dueOptions.noWorkingTime}
      onOutcome={withOutcome(item) && !item.outcomeRequired ? () => openOutcome(item) : null}
      snoozeSignal={snoozeSignal?.id === item.id ? snoozeSignal.n : undefined}
      onFocus={() => setFocusId(item.id)}
      onComplete={() => complete(item)}
      onSnooze={(due) => snooze(item, due)}
      onCall={() => call(item)}
      onOpen={() => openRecord(item)}
    />
  );

  const waiting = data.waiting;
  const calls = source.calls ? <NoOutcomeCalls now={now} /> : null;
  if (order.length === 0 && done.length === 0 && (waiting?.items.length ?? 0) === 0) {
    return (
      <>
        <StateView icon="check-circle" title={t.emptyTodayTitle} />
        {calls}
      </>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: делегування клавіш блукаючого списку задач (keyboard.md, розд. 6)
    <div ref={listRef} onKeyDown={onKeyDown} role="presentation">
      {overdue.length > 0 && (
        <Group title={t.overdue} count={overdueCount.count} capped={overdueCount.capped} danger>
          {overdue.map((x) => row(x, 'overdue'))}
        </Group>
      )}
      {order.length === 0 ? (
        <StateView icon="check-circle" title={t.emptyTodayTitle} />
      ) : (
        <Group title={t.scheduled} count={todayCount.count} capped={todayCount.capped}>
          {today.map((x, i) => row(x, 'today', i === nowIndex))}
        </Group>
      )}
      {waiting && waiting.items.length > 0 && (
        <Group
          title={t.waiting}
          count={waiting.count.count}
          capped={waiting.count.capped}
          danger={waiting.items.some((x) => x.state === 'breached')}
        >
          {waiting.items.map((item) => (
            <WaitingRow
              key={`${item.record.entity}:${item.record.id}`}
              item={item}
              onOpen={() =>
                void navigate({
                  to: '/r/$entity/$recordId',
                  params: { entity: item.record.entity, recordId: item.record.id },
                })
              }
            />
          ))}
        </Group>
      )}
      {calls}
      {done.length > 0 && (
        <Group
          title={t.done}
          count={doneCount.count}
          capped={doneCount.capped}
          collapsed={!doneOpen}
          onToggle={() => setDoneOpen((o) => !o)}
        >
          {done.map((x) => row(x, 'done'))}
        </Group>
      )}
      {outcomeFor && (
        <Suspense fallback={null}>
          <TaskOutcomeSheet
            task={outcomeFor}
            context={contextOf(outcomeFor)}
            zone={data.timeZone}
            baseOptions={data.dueOptions}
            now={now}
            onCompleted={(result) => completed(outcomeFor, result)}
            onReschedule={
              outcomeFor.actions.includes('reschedule') ? (due) => snooze(outcomeFor, due) : null
            }
            onClose={() => {
              setOutcomeFor(null);
              focusTask(outcomeFor.id);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * «Дзвінки без результату» (Г2-W1; calls.md, розд. 4): мої дзвінки за 7 днів, найновіші зверху.
 * Секція є, лише коли в `/meta` є сутність з поведінкою `calls`; порожньо — секції немає. Рядок
 * відкриває картку господаря з блоком результату (`?call=<id>`), дзвінок без господаря — власну
 * картку. Збій — рядок з «Ще раз», решта панелі працює.
 */
function NoOutcomeCalls({ now }: { now: number }) {
  const navigate = useNavigate();
  const meta = usePlatformMeta({ optional: true });
  const entity = callEntityOf(meta.data);
  const key = entity?.key ?? '';
  const query = useQuery({
    queryKey: noOutcomeKey(key),
    queryFn: ({ signal }) => fetchNoOutcome(key, signal),
    enabled: entity !== undefined,
  });
  if (!entity) return null;
  if (query.isError && !query.data) {
    return (
      <p className="today__group small muted" role="status">
        {t.noOutcomeFailed}{' '}
        <button className="link-btn" type="button" onClick={() => void query.refetch()}>
          {tc.retry}
        </button>
      </p>
    );
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) return null;
  const open = (row: RecordRow) => {
    const v = row.values;
    const host =
      typeof v.host_entity === 'string' && typeof v.host === 'string'
        ? { entity: v.host_entity, id: v.host }
        : { entity: key, id: row.id };
    void navigate({
      to: '/r/$entity/$recordId',
      params: { entity: host.entity, recordId: host.id },
      search: { call: row.id },
    });
  };
  return (
    <Group
      title={t.noOutcome}
      count={items.length}
      capped={items.length >= NO_OUTCOME_LIMIT && query.data?.nextCursor != null}
    >
      {items.map((row) => (
        <NoOutcomeRow key={row.id} row={row} now={now} onOpen={() => open(row)} />
      ))}
    </Group>
  );
}

function NoOutcomeRow({ row, now, onOpen }: { row: RecordRow; now: number; onOpen: () => void }) {
  const v = row.values;
  const at = typeof v.started_at === 'string' ? v.started_at : null;
  const number =
    typeof v.client_phone === 'string'
      ? v.client_phone
      : typeof v.client_phone_raw === 'string'
        ? v.client_phone_raw
        : null;
  const title = number ?? t.category.call;
  const ctx = typeof v.host === 'string' ? null : t.noOutcomeUnknown;
  const time = at
    ? dayDiff(at, now) < 0
      ? formatOverdueLabel(at, now)
      : formatTime(at)
    : tc.noTime;
  return (
    <li className="task" data-call-row={row.id}>
      <span />
      <span className="task__time">{time}</span>
      <span className="task__main">
        <button
          className="link-btn task__title row-flex"
          type="button"
          aria-label={t.noOutcomeOpen(title)}
          onClick={onOpen}
        >
          <Icon name="phone" />
          <span className="truncate num">{title}</span>
        </button>
        {ctx && <span className="task__ctx">{ctx}</span>}
      </span>
      <span className="task__actions" />
    </li>
  );
}

/**
 * Рядок контексту панелі результату — «тема · господар» (tasks.html, В1): тема вже містить людину
 * («Подзвонити: Мар'яна Гнатюк»), тож корінь окремо не додаємо. Тема прихована — назва категорії.
 */
function contextOf(item: TodayItem): string {
  return [item.subject ?? t.category[item.category], item.host?.label].filter(Boolean).join(' · ');
}

interface GroupProps {
  title: string;
  count: number;
  capped?: boolean;
  danger?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

function Group({ title, count, capped, danger, collapsed, onToggle, children }: GroupProps) {
  const head = (
    <>
      <Icon name={collapsed ? 'chevron-right' : 'chevron-down'} />
      <span className={danger ? 'today__danger' : undefined}>{title}</span>
      <span className={danger ? 'count count--danger' : 'count'}>
        {capped ? `${NAV_COUNT_MAX}+` : count}
      </span>
    </>
  );
  return (
    <div className="today__group">
      <h3 className="today__group-title">
        {onToggle ? (
          <button
            className="today__section-head"
            type="button"
            aria-expanded={!collapsed}
            onClick={onToggle}
          >
            {head}
          </button>
        ) : (
          <span className="today__section-head">{head}</span>
        )}
      </h3>
      {!collapsed && <ul className="today__list">{children}</ul>}
    </div>
  );
}

interface TaskRowProps {
  item: TodayItem;
  overdue: boolean;
  isNow: boolean;
  active: boolean;
  now: number;
  choices: DueChoice[];
  noWorkingTime: boolean;
  snoozeSignal: number | undefined;
  /** «Виконати з результатом…» для типу з необов'язковим результатом; `null` — пункту немає. */
  onOutcome: (() => void) | null;
  onFocus: () => void;
  onComplete: () => void;
  onSnooze: (due: TodayDue) => void;
  onCall: () => void;
  onOpen: () => void;
}

const CHOICE_LABEL = { inHour: t.dueInHour, tomorrow: t.dueTomorrow, nextMonday: t.dueNextMonday };

/** Підпис варіанта: «Завтра, 14:00» — час доби задачі лишається; «на весь день» — без часу. */
function choiceLabel(choice: DueChoice): string {
  const base = CHOICE_LABEL[choice.kind];
  return 'dueAt' in choice.due ? `${base}, ${formatTime(choice.due.dueAt)}` : base;
}

function TaskRow(props: TaskRowProps) {
  const { item, overdue, isNow, active, now } = props;
  const done = item.state === 'done';
  const time = timeOf(item, overdue, now);
  const { title, prefix, ctx } = describeItem(item);
  const cls = [
    'task',
    overdue && 'task--overdue',
    done && 'task--done',
    isNow && 'task--now',
    active && 'is-focused',
  ]
    .filter(Boolean)
    .join(' ');
  const canComplete = !done && item.actions.includes('complete');
  return (
    <li
      className={cls}
      data-task={item.id}
      data-roving
      tabIndex={active ? 0 : -1}
      onFocus={props.onFocus}
      onDoubleClick={props.onOpen}
      aria-label={`${overdue ? `${t.overdue}: ` : ''}${time.spoken} · ${prefix ? `${prefix}: ` : ''}${title}${ctx ? ` · ${ctx}` : ''}`}
    >
      {canComplete ? (
        <button
          className="task__done"
          type="button"
          tabIndex={-1}
          aria-label={`${t.completeTask}: ${prefix ?? title}`}
          onClick={props.onComplete}
        />
      ) : done ? (
        <span className="task__done" role="img" aria-label={`${t.completedTask}: ${title}`} />
      ) : (
        <span />
      )}
      <span className="task__time">{time.shown}</span>
      <span className="task__main">
        <span className="task__title row-flex">
          <Icon name={CATEGORY_ICON[item.category]} />
          <span className="truncate">
            {prefix && <span className="sr-only">{`${prefix}: `}</span>}
            {title}
          </span>
        </span>
        {ctx && <span className="task__ctx">{ctx}</span>}
      </span>
      <span className="task__actions">
        {item.phone && (
          <a
            className="phone__call"
            href={telHref(item.phone)}
            aria-label={`${tl.call}: ${title}`}
            tabIndex={-1}
            data-call
            onClick={() => {
              startCall(item.phone ?? '');
              props.onCall();
            }}
          >
            <Icon name="phone" />
          </a>
        )}
        {props.onOutcome && (
          <MenuButton
            label={`${t.moreTaskActions}: ${prefix ?? title}`}
            className="btn btn--ghost btn--icon btn--sm"
            items={[
              {
                kind: 'item' as const,
                id: 'outcome',
                label: t.completeWithOutcome,
                hint: 'Shift+X',
                onSelect: () => props.onOutcome?.(),
              },
            ]}
          >
            <Icon name="more" />
          </MenuButton>
        )}
        {props.choices.length > 0 && (
          <MenuButton
            label={`${t.snooze}: ${prefix ?? title}`}
            className="btn btn--ghost btn--icon btn--sm task__snooze"
            openSignal={props.snoozeSignal}
            items={[
              // Робочого часу немає — варіанти за календарем: пояснення першим рядком меню.
              ...(props.noWorkingTime
                ? [{ kind: 'group' as const, id: 'no-working-time', label: t.dueNoWorkingTime }]
                : []),
              ...props.choices.map((choice) => ({
                kind: 'item' as const,
                id: choice.key,
                label: choiceLabel(choice),
                hint: choice.key,
                onSelect: () => props.onSnooze(choice.due),
              })),
            ]}
          >
            <Icon name="clock" />
          </MenuButton>
        )}
      </span>
    </li>
  );
}

/**
 * Бейдж першої реакції (components.md, розд. 11; tasks-sla.md, розд. 4): колір завжди з іконкою і
 * словом (WCAG 1.4.1). Порушення — `role="status"`, без звуку (NTF-19).
 */
export function SlaBadge({ item }: { item: WaitingItem }) {
  const left = item.remainingWorkingMinutes;
  if (item.state === 'breached') {
    return (
      <span className="badge badge--danger" role="status">
        <Icon name="alert-circle" />
        {left !== null && left < 0 ? t.slaBreachedBy(t.workMinutes(-left)) : t.slaBreached}
      </span>
    );
  }
  if (item.state === 'warning') {
    return (
      <span className="badge badge--warn">
        <Icon name="clock" />
        {left !== null && left >= 0 ? t.slaWarningLeft(t.workMinutes(left)) : t.slaWarning}
      </span>
    );
  }
  return left !== null && left >= 0 ? (
    <span className="badge">{t.slaLeft(t.workMinutes(left))}</span>
  ) : null;
}

function WaitingRow({ item, onOpen }: { item: WaitingItem; onOpen: () => void }) {
  const phone = item.phone;
  const name = item.record.label ?? item.root?.label ?? t.category.task;
  return (
    <li className={item.state === 'breached' ? 'task task--overdue' : 'task'}>
      <span />
      <span className="task__time">{formatTime(item.startedAt)}</span>
      <span className="task__main">
        <button className="link-btn task__title strong" type="button" onClick={onOpen}>
          {name}
        </button>
        <span className="task__ctx">
          <SlaBadge item={item} />
        </span>
      </span>
      <span className="task__actions">
        {phone && (
          <a
            className="phone__call"
            href={telHref(phone)}
            aria-label={`${tl.call}: ${name}`}
            onClick={() => startCall(phone)}
          >
            <Icon name="phone" />
          </a>
        )}
      </span>
    </li>
  );
}

function TodaySkeleton() {
  return (
    <div className="sk-group today__skeleton" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="task">
          <span className="sk sk--circle" />
          <span className="sk sk--line" />
          <span className="sk sk--line" />
        </div>
      ))}
    </div>
  );
}
